import type { CountryConfig } from './types'
import { uaeTaxStrategy } from './strategies/uaeTaxStrategy'
import { uaeAssetStrategy } from './strategies/uaeAssetStrategy'

export const uaeConfig: CountryConfig = {
  code: 'UAE',
  name: 'United Arab Emirates',
  flag: '🇦🇪',

  currency: {
    code: 'AED',
    symbol: 'د.إ',
    name: 'UAE Dirham',
    locale: 'en-AE', // Use en-AE for better number formatting
    defaultExchangeRate: 22.7, // AED to INR
    decimalPlaces: 0,
  },

  taxRules: {
    hasIncomeTax: false, // Key difference from USA
    brackets: [], // No income tax brackets
    ltcgRate: 0,
    stcgRate: 0,
    ltcgThresholdDays: 0, // Not applicable
    exemptions: [],
    dtaaBenefits: [
      {
        id: 'no-income-tax',
        description: 'No personal income tax in UAE',
        applicableTo: ['*']
      },
      {
        id: 'india-dtaa',
        article: 'UAE-India DTAA',
        description: 'Income earned in UAE is not taxed. On return to India, RNOR status provides tax benefits.',
        applicableTo: ['salary', 'investments']
      },
    ],
    requiredForms: [
      {
        id: 'emirates-id',
        name: 'Emirates ID',
        description: 'Required for banking and official transactions',
        required: true
      },
      {
        id: 'visa-docs',
        name: 'Residence Visa',
        description: 'Valid residence visa documentation',
        required: true
      },
      {
        id: 'salary-certificate',
        name: 'Salary Certificate',
        description: 'For EOSB calculation and loan applications',
        required: false
      },
    ],
    specialRules: {
      'corporate-tax': '9% corporate tax applies to businesses with profits over AED 375,000 (since June 2023)',
      'golden-visa': 'Property investment of AED 2M+ qualifies for 10-year Golden Visa',
      'vat': '5% VAT applies to most goods and services',
    },
  },

  assets: {
    retirement: [], // UAE has no retirement accounts like 401(k)/IRA

    investment: [
      {
        id: 'savingsAccount',
        name: 'Savings Account',
        description: 'Bank savings account',
        icon: '🏦',
        category: 'investment',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: true,
        fieldMapping: 'savingsAccount',
        helpText: 'No tax on interest income in UAE. Consider high-yield options.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'stocks',
        name: 'Stock Investments',
        description: 'Direct stock holdings (DFM, ADX, international)',
        icon: '📈',
        category: 'investment',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: true,
        fieldMapping: 'stocks',
        helpText: 'No capital gains tax in UAE. Note: May be taxed in India after return.',
        sortOrder: 2,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'mutualFunds',
        name: 'Mutual Funds',
        description: 'Investment funds and ETFs',
        icon: '📊',
        category: 'investment',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'mutualFunds',
        helpText: 'No tax on fund gains in UAE. Consider global diversification.',
        sortOrder: 3,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'fixedDeposits',
        name: 'Fixed Deposits',
        description: 'Term deposits with banks',
        icon: '💰',
        category: 'investment',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'fixedDeposits',
        helpText: 'Tax-free interest income. Compare rates across banks.',
        sortOrder: 4,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],

    employerEquity: [
      {
        id: 'eosb',
        name: 'End of Service Benefits (EOSB)',
        description: 'Gratuity based on years of service',
        icon: '🎁',
        category: 'employerEquity',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: true,
        fieldMapping: 'eosbBalance',
        helpText: 'Calculated as: 21 days basic salary × years (first 5 yrs) + 30 days × years (after 5 yrs). Max 2 years total salary.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'companyStock',
        name: 'Company Stock/Options',
        description: 'Employer equity compensation',
        icon: '🏢',
        category: 'employerEquity',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: true,
        fieldMapping: 'companyStock',
        helpText: 'Stock grants from employer. No tax in UAE.',
        sortOrder: 2,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],

    realEstate: [
      {
        id: 'uaeProperty',
        name: 'UAE Property',
        description: 'Real estate in UAE (equity value)',
        icon: '🏠',
        category: 'realEstate',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'uaePropertyEquity',
        helpText: 'No property tax in most emirates. AED 2M+ investment qualifies for Golden Visa.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],

    cash: [
      {
        id: 'currentAccount',
        name: 'Current Account',
        description: 'Bank current/checking account',
        icon: '💵',
        category: 'cash',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'currentAccount',
        helpText: 'AED or multi-currency account. No tax on balances.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],

    benefits: [
      {
        id: 'pension',
        name: 'Pension (if applicable)',
        description: 'Government or private pension',
        icon: '🏛️',
        category: 'benefits',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'pensionBenefit',
        helpText: 'UAE nationals may have GPSSA pension. Expats typically rely on EOSB.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],
  },

  benefits: {
    hasSocialSecurity: false,
    hasEOSB: true,
    eosbRules: {
      firstFiveYearsRate: 21,    // 21 days per year
      afterFiveYearsRate: 30,    // 30 days per year
      maxYearsCapped: 2,         // Max 2 years of basic salary
      basedOn: 'basic-salary',   // Excludes allowances
    },
  },

  labels: {
    retirementSection: 'Employment Benefits',
    investmentSection: 'Investments & Savings',
    employerEquitySection: 'Employer Benefits',
    realEstateSection: 'Real Estate',
    cashSection: 'Bank Accounts',
    benefitsSection: 'Other Benefits',
    incomeLabel: 'Annual Income (AED)',
    expenseLabel: 'Monthly Expenses (AED)',
    savingsLabel: 'Total Savings',
    netWorthLabel: 'Net Worth',
    taxImplicationsLabel: 'Tax Considerations',
    capitalGainsLabel: 'Capital Gains',
  },

  helpText: {
    retirementAccounts: 'UAE does not have tax-advantaged retirement accounts. EOSB is the primary end-of-service benefit.',
    taxImplications: 'No personal income tax in UAE. Plan for RNOR status benefits when returning to India.',
    dtaaBenefits: 'UAE-India DTAA ensures income is not double-taxed. RNOR status in India provides additional benefits.',
    withdrawalPenalties: 'No withdrawal penalties in UAE. EOSB is paid upon end of service.',
    eosb: 'End of Service Benefits are calculated based on basic salary only. Allowances are excluded.',
  },

  taxStrategy: uaeTaxStrategy,
  assetStrategy: uaeAssetStrategy,
}
